import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import EnterNavForm from '../../components/EnterNavForm';
import { styles, colors, radius, shadows } from '../../styles/theme';
import { normalizeForSearch, searchMatchPre } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// Kategori string'ini 3 ana gruba (Yarı Mamul / Tabak / Hammadde) eşler ve görsel yapı döndürür.
// Diğer kategoriler (BAHARAT, MANAV vb) "Hammadde" alt-tipi olarak gösterilir; bilinmeyen değer
// ise serbest gri rozet olur (yine de görünür kalır).
const KATEGORI_GRUP = {
  YARI_MAMUL: { bg: '#fff3e0', fg: '#e65100', label: 'Yarı Mamul' },
  TABAK: { bg: '#e8eaf6', fg: '#3f51b5', label: 'Tabak' },
  HAMMADDE: { bg: '#e0f2f1', fg: '#00695c', label: 'Hammadde' },
  DIGER: { bg: '#f5f5f5', fg: '#666' },
};

const kategoriRozet = (kategori) => {
  if (!kategori) return null;
  const k = String(kategori).toLowerCase().trim();
  // Yarı mamul varyantları (URUN ve ARA_URUN eski sistemden kalma yarı mamul kayıtları)
  if (['yari mamul', 'yarı mamul', 'urun', 'ara_urun', 'iç reçete / yarı mamul', 'kasap ürünü', 'et üretim', 'i̇ç reçete / yarı mamul'].includes(k)) {
    return KATEGORI_GRUP.YARI_MAMUL;
  }
  if (['mamul'].includes(k)) {
    return KATEGORI_GRUP.TABAK;
  }
  // Hammadde grubu — ana 'hammadde' + tüm alt-tipler (Manav, ET, BAHARAT, MARKET, AMBALAJ, SOS, vb)
  const hammaddeAltTipleri = [
    'hammadde', 'et', 'baharat', 'manav', 'market', 'ambalaj', 'sut_urun', 'süt ürünleri',
    'sos', 'ekmek', 'dana eti', 'kuzu eti', 'tavuk', 'tavuk eti', 'sirke/sos', 'kuru gıda',
    'tatlandırıcı', 'donuk/i̇şlenmiş', 'donuk/işlenmiş', 'tursu_sirke', 'turşu', 'i̇çecek', 'içecek',
    'salata hazırlık', 'tatlı', 'tatlı hazırlık', 'yağlar', 'hayvansal yağ', 'bakliyat/tahıl',
    'fırın', 'karkas', 'süt ürünler', 'temizlik/sarf', 'temizlik',
  ];
  if (hammaddeAltTipleri.includes(k)) {
    return { ...KATEGORI_GRUP.HAMMADDE, label: kategori }; // alt tipi koru (ör. 'BAHARAT')
  }
  // Bilinmeyen — kategori adını olduğu gibi göster
  return { ...KATEGORI_GRUP.DIGER, label: kategori };
};

const StokSayimPage = () => {
  useAuth();
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [selectedLokasyon, setSelectedLokasyon] = useState('');
  const [stoklar, setStoklar] = useState([]);
  const [sayimData, setSayimData] = useState({});
  const [aciklamaData, setAciklamaData] = useState({});
  const [genelAciklama, setGenelAciklama] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [sadeceFarkli, setSadeceFarkli] = useState(false);
  const [arama, setArama] = useState('');
  const [sonuc, setSonuc] = useState(null);
  const [sayimId, setSayimId] = useState(null);
  const [kaydDurumu, setKaydDurumu] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [taslakBanner, setTaslakBanner] = useState(null); // null | { sayim_id, baslangic_tarihi }
  const saveTimerRef = useRef({});

  const hasTutarOzeti = sonuc && (typeof sonuc.toplam_fazla_tutar === 'number' || typeof sonuc.toplam_eksik_tutar === 'number');
  const showTutarColumn = sonuc && sonuc.detay && sonuc.detay.some(d => typeof d.fark_tutari === 'number');

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    fetch(`${API_URL}/api/lokasyonlar`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => setLokasyonlar(Array.isArray(data) ? data : []))
      .catch(() => setLokasyonlar([]));
  }, []);

  const loadStoklar = useCallback(async (lokasyonId) => {
    if (!lokasyonId) {
      setStoklar([]);
      setSayimData({});
      setAciklamaData({});
      return;
    }
    setLoading(true);
    setMessage({ text: '', type: '' });
    setSonuc(null);
    try {
      const token = localStorage.getItem('access_token');
      // include_unstocked=true: o lokasyonda Stok satırı olmayan aktif malzemeler
      // de dropdown'a düşsün — yeni eklenmiş ürün, başka şubeden gelmemiş ama sayım
      // sırasında elinizde olabilen ürünler için.
      const res = await fetch(`${API_URL}/api/stok?lokasyon_id=${lokasyonId}&include_unstocked=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      setStoklar(list);
      setSayimData({});
      setAciklamaData({});
    } catch {
      setStoklar([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLokasyonChange = async (e) => {
    const val = e.target.value;
    setSelectedLokasyon(val);
    setSayimId(null);
    setStoklar([]);
    setSayimData({});
    setAciklamaData({});
    setTaslakBanner(null);
    setSonuc(null);
    setMessage({ text: '', type: '' });
    Object.values(saveTimerRef.current).forEach(clearTimeout);
    saveTimerRef.current = {};
    if (!val) return;
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`${API_URL}/api/stok-sayim/oturum/aktif?lokasyon_id=${val}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const taslak = await res.json();
        setTaslakBanner({ sayim_id: taslak.sayim_id, baslangic_tarihi: taslak.baslangic_tarihi });
      } else {
        await loadStoklar(val);
      }
    } catch {
      await loadStoklar(val);
    }
  };

  const handleDevamEt = async (devamSayimId) => {
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`${API_URL}/api/stok-sayim/oturum/${devamSayimId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) { setMessage({ text: 'Sayım yüklenemedi', type: 'error' }); return; }
      const data = await res.json();
      setSayimId(devamSayimId);
      setTaslakBanner(null);
      // Convert kalemler to stoklar format
      const stokList = data.kalemler.map(k => ({
        malzeme_id: k.malzeme_id,
        malzeme_ad: k.malzeme_ad,
        malzeme_kod: k.malzeme_kod,
        birim: k.birim,
        miktar: k.sistem_snapshot_miktar,
      }));
      setStoklar(stokList);
      // Restore saved sayilan_miktar values
      const savedData = {};
      const savedAciklama = {};
      data.kalemler.forEach(k => {
        if (k.sayilan_miktar !== null && k.sayilan_miktar !== undefined) {
          savedData[k.malzeme_id] = String(k.sayilan_miktar);
        }
        if (k.aciklama) savedAciklama[k.malzeme_id] = k.aciklama;
      });
      setSayimData(savedData);
      setAciklamaData(savedAciklama);
    } catch (err) {
      setMessage({ text: 'Sunucu hatası: ' + err.message, type: 'error' });
    }
  };

  const handleYeniBaslat = async () => {
    const token = localStorage.getItem('access_token');
    try {
      if (taslakBanner) {
        await fetch(`${API_URL}/api/stok-sayim/oturum/${taslakBanner.sayim_id}/iptal`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }
        });
      }
      const res = await fetch(`${API_URL}/api/stok-sayim/oturum`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ lokasyon_id: parseInt(selectedLokasyon), aciklama: genelAciklama || null }),
      });
      if (!res.ok) { const err = await res.json(); setMessage({ text: err.detail || 'Sayım başlatılamadı', type: 'error' }); return; }
      const body = await res.json();
      setSayimId(body.sayim_id);
      setTaslakBanner(null);
      await loadStoklar(selectedLokasyon);
    } catch (err) {
      setMessage({ text: 'Sunucu hatası: ' + err.message, type: 'error' });
    }
  };

  const handleSayilanChange = (malzemeId, value) => {
    setSayimData(prev => ({ ...prev, [malzemeId]: value }));
    if (!sayimId) return;
    clearTimeout(saveTimerRef.current[malzemeId]);
    setKaydDurumu('saving');
    saveTimerRef.current[malzemeId] = setTimeout(async () => {
      const token = localStorage.getItem('access_token');
      try {
        const patchRes = await fetch(`${API_URL}/api/stok-sayim/oturum/${sayimId}/kalem/${malzemeId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ sayilan_miktar: value === '' ? null : parseFloat(value) }),
        });
        if (patchRes.ok) setKaydDurumu('saved');
        else setKaydDurumu('error');
      } catch {
        setKaydDurumu('error');
      }
    }, 600);
  };

  const handleAciklamaChange = (malzemeId, value) => {
    setAciklamaData(prev => ({ ...prev, [malzemeId]: value }));
  };

  const getFark = (malzemeId, sistemStok) => {
    const sayilan = sayimData[malzemeId];
    if (sayilan === '' || sayilan === undefined || sayilan === null) return null;
    return parseFloat(sayilan) - sistemStok;
  };

  // Filtreleme
  const filteredStoklar = stoklar.filter(s => {
    if (arama) {
      const lower = normalizeForSearch(arama);
      const adMatch = searchMatchPre(s.malzeme_ad, lower);
      const kodMatch = searchMatchPre(s.malzeme_kod, lower);
      if (!adMatch && !kodMatch) return false;
    }
    if (sadeceFarkli) {
      const fark = getFark(s.malzeme_id, s.miktar);
      if (fark === null || Math.abs(fark) < 0.001) return false;
    }
    return true;
  });

  // Özet hesapla
  const sayilanKalem = Object.keys(sayimData).filter(k => sayimData[k] !== '' && sayimData[k] !== undefined).length;
  const farkliKalemler = stoklar.filter(s => {
    const fark = getFark(s.malzeme_id, s.miktar);
    return fark !== null && Math.abs(fark) >= 0.001;
  });
  const toplamFazla = farkliKalemler.reduce((sum, s) => {
    const fark = getFark(s.malzeme_id, s.miktar);
    return fark > 0 ? sum + fark : sum;
  }, 0);
  const toplamEksik = farkliKalemler.reduce((sum, s) => {
    const fark = getFark(s.malzeme_id, s.miktar);
    return fark < 0 ? sum + fark : sum;
  }, 0);

  const handleTamamla = async () => {
    if (!sayimId) { setMessage({ text: 'Önce sayımı başlatınız', type: 'error' }); return; }
    const sayilanKalemler = stoklar.filter(s => {
      const v = sayimData[s.malzeme_id];
      return v !== '' && v !== undefined && v !== null;
    });
    if (sayilanKalemler.length === 0) {
      setMessage({ text: 'En az bir malzeme için sayılan miktar giriniz', type: 'error' }); return;
    }
    const sifirlanacak = sayilanKalemler.filter(s => parseFloat(sayimData[s.malzeme_id]) === 0 && s.miktar > 0);
    if (sifirlanacak.length > 0) {
      const adlar = sifirlanacak.map(s => s.malzeme_ad).join(', ');
      if (!window.confirm(`${sifirlanacak.length} malzemenin stoğu sıfırlanacak:\n${adlar}\n\nEmin misiniz?`)) return;
    }
    Object.values(saveTimerRef.current).forEach(clearTimeout);
    saveTimerRef.current = {};
    setSaving(true);
    setMessage({ text: '', type: '' });
    setSonuc(null);
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(`${API_URL}/api/stok-sayim/oturum/${sayimId}/tamamla`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      const result = await res.json();
      if (res.ok) {
        setSonuc(result);
        setMessage({ text: result.mesaj, type: 'success' });
        setSayimId(null);
        setKaydDurumu('idle');
        loadStoklar(selectedLokasyon);
        setGenelAciklama('');
      } else {
        setMessage({ text: result.detail || 'Hata oluştu', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Sunucu hatası: ' + err.message, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleIptal = async () => {
    if (!sayimId) return;
    if (!window.confirm('Sayım iptal edilecek. Girilen tüm veriler silinecek. Devam etmek istiyor musunuz?')) return;
    Object.values(saveTimerRef.current).forEach(clearTimeout);
    saveTimerRef.current = {};
    const token = localStorage.getItem('access_token');
    try {
      const res = await fetch(`${API_URL}/api/stok-sayim/oturum/${sayimId}/iptal`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        setSayimId(null);
        setKaydDurumu('idle');
        setStoklar([]);
        setSayimData({});
        setAciklamaData({});
        setMessage({ text: 'Sayım iptal edildi', type: 'info' });
      } else {
        const errData = await res.json().catch(() => ({}));
        setMessage({ text: errData.detail || 'İptal edilemedi', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: 'Sunucu hatası: ' + err.message, type: 'error' });
    }
  };

  // Tümünü doldur kısayolu
  const handleTumunuAyniYap = () => {
    stoklar.forEach(s => {
      handleSayilanChange(s.malzeme_id, String(s.miktar));
    });
  };

  return (
    <div>
      <Header title="Stok Sayım" />
      <div style={{ padding: '20px' }}>

        {/* Mesaj */}
        {message.text && (
          <div style={{
            padding: '12px 16px', marginBottom: '15px', borderRadius: radius.md,
            background: message.type === 'success' ? '#d4edda' : message.type === 'info' ? '#d1ecf1' : '#f8d7da',
            color: message.type === 'success' ? '#155724' : message.type === 'info' ? '#0c5460' : '#721c24',
            border: `1px solid ${message.type === 'success' ? '#c3e6cb' : message.type === 'info' ? '#bee5eb' : '#f5c6cb'}`
          }}>
            {message.text}
          </div>
        )}

        {/* Taslak Banner */}
        {taslakBanner && !sayimId && (
          <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>
              <strong>Devam eden sayım var:</strong>{' '}
              {new Date(taslakBanner.baslangic_tarihi).toLocaleString('tr-TR')} tarihinde başladı
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleDevamEt(taslakBanner.sayim_id)} style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: '13px' }}>
                Devam Et
              </button>
              <button onClick={handleYeniBaslat} style={{ background: '#fff', color: '#555', border: '1px solid #ccc', borderRadius: 6, padding: '7px 16px', cursor: 'pointer', fontSize: '13px' }}>
                Yeni Başlat
              </button>
            </div>
          </div>
        )}

        {/* Sayım Sonucu Detay */}
        {sonuc && sonuc.detay && sonuc.detay.length > 0 && (
          <div style={{
            background: '#e8f5e9', borderRadius: radius.lg, padding: '20px',
            marginBottom: '20px', border: '1px solid #a5d6a7'
          }}>
            <h4 style={{ margin: '0 0 12px', color: '#2e7d32' }}>Sayım Sonucu</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '12px' }}>
              <div style={sonucKartStyle}>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Güncellenen</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: styles.secondary }}>{sonuc.guncellenen_kalem}</div>
              </div>
              <div style={sonucKartStyle}>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Değişmeyen</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: colors.textTertiary }}>{sonuc.degismeyen_kalem}</div>
              </div>
              <div style={sonucKartStyle}>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Toplam Fazla</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: styles.success }}>+{sonuc.toplam_fazla}</div>
              </div>
              <div style={sonucKartStyle}>
                <div style={{ fontSize: '11px', color: colors.textSecondary }}>Toplam Eksik</div>
                <div style={{ fontSize: '20px', fontWeight: 'bold', color: styles.danger }}>{sonuc.toplam_eksik}</div>
              </div>
              {hasTutarOzeti && (
                <div style={sonucKartStyle}>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>Toplam Fazla (₺)</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: styles.success }}>
                    {Number(sonuc.toplam_fazla_tutar || 0).toFixed(2)}
                  </div>
                </div>
              )}
              {hasTutarOzeti && (
                <div style={sonucKartStyle}>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>Toplam Eksik (₺)</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: styles.danger }}>
                    {Number(sonuc.toplam_eksik_tutar || 0).toFixed(2)}
                  </div>
                </div>
              )}
              {hasTutarOzeti && (
                <div style={sonucKartStyle}>
                  <div style={{ fontSize: '11px', color: colors.textSecondary }}>Net Etki (₺)</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: (sonuc.net_tutar || 0) >= 0 ? styles.success : styles.danger }}>
                    {Number(sonuc.net_tutar || 0).toFixed(2)}
                  </div>
                </div>
              )}
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#c8e6c9' }}>
                  <th style={thStyleSmall}>Malzeme</th>
                  <th style={{ ...thStyleSmall, textAlign: 'right' }}>Önceki</th>
                  <th style={{ ...thStyleSmall, textAlign: 'right' }}>Sayılan</th>
                  <th style={{ ...thStyleSmall, textAlign: 'right' }}>Fark</th>
                  {showTutarColumn && <th style={{ ...thStyleSmall, textAlign: 'right' }}>Birim Maliyet</th>}
                  {showTutarColumn && <th style={{ ...thStyleSmall, textAlign: 'right' }}>Fark Tutarı</th>}
                  <th style={{ ...thStyleSmall, textAlign: 'right' }}>Yeni Stok</th>
                </tr>
              </thead>
              <tbody>
                {sonuc.detay.map((d, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={tdStyleSmall}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {d.malzeme_kod && <span style={{ color: '#555', fontSize: '10px', fontFamily: 'monospace', fontWeight: 600, padding: '1px 5px', background: '#eef2f5', borderRadius: 4 }}>{d.malzeme_kod}</span>}
                        <span>{d.malzeme_ad}</span>
                        {(() => {
                          const conf = kategoriRozet(d.kategori);
                          return conf ? (
                            <span style={{
                              fontSize: '9px', fontWeight: 600, padding: '1px 5px', borderRadius: 8,
                              background: conf.bg, color: conf.fg,
                            }}>{conf.label}</span>
                          ) : null;
                        })()}
                      </div>
                      <span style={{ color: colors.textTertiary, fontSize: '11px', marginLeft: '4px' }}>({d.birim})</span>
                    </td>
                    <td style={{ ...tdStyleSmall, textAlign: 'right' }}>{d.onceki_stok.toFixed(2)}</td>
                    <td style={{ ...tdStyleSmall, textAlign: 'right' }}>{d.sayilan.toFixed(2)}</td>
                    <td style={{
                      ...tdStyleSmall, textAlign: 'right', fontWeight: 'bold',
                      color: d.fark > 0 ? styles.success : d.fark < 0 ? styles.danger : '#999'
                    }}>
                      {d.fark > 0 ? '+' : ''}{d.fark.toFixed(2)}
                    </td>
                    {showTutarColumn && (
                      <td style={{ ...tdStyleSmall, textAlign: 'right' }}>
                        {d.birim_maliyet != null ? Number(d.birim_maliyet).toFixed(2) : '-'}
                      </td>
                    )}
                    {showTutarColumn && (
                      <td style={{
                        ...tdStyleSmall, textAlign: 'right', fontWeight: 'bold',
                        color: d.fark_tutari > 0 ? styles.success : d.fark_tutari < 0 ? styles.danger : '#999'
                      }}>
                        {d.fark_tutari > 0 ? '+' : ''}{Number(d.fark_tutari || 0).toFixed(2)}
                      </td>
                    )}
                    <td style={{ ...tdStyleSmall, textAlign: 'right', fontWeight: 'bold' }}>{d.yeni_stok.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Sayım Formu */}
        <div style={{
          background: colors.surface, borderRadius: radius.lg, padding: '24px',
          boxShadow: shadows.md, marginBottom: '20px'
        }}>
          <h3 style={{ margin: '0 0 20px', color: styles.secondary }}>Sayım Bilgileri</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '20px' }}>
            <div>
              <label style={labelStyle}>Lokasyon *</label>
              <select value={selectedLokasyon} onChange={handleLokasyonChange} style={inputStyle}>
                <option value="">Lokasyon Seçiniz</option>
                {lokasyonlar.map(l => (
                  <option key={l.id} value={l.id}>{l.ad}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Sayım Notu</label>
              <input
                type="text" value={genelAciklama}
                onChange={(e) => setGenelAciklama(e.target.value)}
                placeholder="Ör: Aylık periyodik sayım..."
                style={inputStyle}
              />
            </div>
          </div>

          {/* Stok tablosu */}
          {selectedLokasyon && (
            <>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: colors.textSecondary }}>Stoklar yükleniyor...</div>
              ) : stoklar.length === 0 && !taslakBanner ? (
                <div style={{ textAlign: 'center', padding: '40px', color: colors.textTertiary }}>Bu lokasyonda stok kaydı bulunamadı</div>
              ) : stoklar.length > 0 ? (
                <>
                  {/* Araç çubuğu */}
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    marginBottom: '12px', gap: '12px', flexWrap: 'wrap'
                  }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="text" value={arama}
                        onChange={(e) => setArama(e.target.value)}
                        placeholder="Malzeme ad veya kod ara..."
                        style={{ ...inputStyle, width: '220px', marginBottom: 0 }}
                      />
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '13px' }}>
                        <input
                          type="checkbox" checked={sadeceFarkli}
                          onChange={(e) => setSadeceFarkli(e.target.checked)}
                          style={{ width: '16px', height: '16px' }}
                        />
                        Sadece farklı olanlar
                      </label>
                      {sayimId && (
                        <span style={{ fontSize: 12, color: kaydDurumu === 'saved' ? '#2e7d32' : kaydDurumu === 'error' ? '#c62828' : '#888', marginLeft: 8 }}>
                          {kaydDurumu === 'saving' && 'Kaydediliyor...'}
                          {kaydDurumu === 'saved' && '✓ Kaydedildi'}
                          {kaydDurumu === 'error' && '⚠ Kaydedilemedi'}
                        </span>
                      )}
                    </div>
                    <button onClick={handleTumunuAyniYap} style={{
                      background: colors.backgroundAlt, color: '#555', border: `1px solid ${colors.border}`,
                      borderRadius: radius.sm, padding: '6px 14px', cursor: 'pointer', fontSize: '12px'
                    }}>
                      Tümüne mevcut miktarı yaz
                    </button>
                  </div>

                  {/* Özet */}
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gap: '10px', marginBottom: '15px'
                  }}>
                    <div style={ozetKartStyle}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary }}>Toplam Kalem</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: styles.secondary }}>{stoklar.length}</div>
                    </div>
                    <div style={ozetKartStyle}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary }}>Sayılan</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: styles.info }}>{sayilanKalem}</div>
                    </div>
                    <div style={ozetKartStyle}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary }}>Fazla Bulunan</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: styles.success }}>
                        {toplamFazla > 0 ? `+${toplamFazla.toFixed(2)}` : '0'}
                      </div>
                    </div>
                    <div style={ozetKartStyle}>
                      <div style={{ fontSize: '11px', color: colors.textSecondary }}>Eksik Bulunan</div>
                      <div style={{ fontSize: '18px', fontWeight: 'bold', color: styles.danger }}>
                        {toplamEksik < 0 ? toplamEksik.toFixed(2) : '0'}
                      </div>
                    </div>
                  </div>

                  {/* Tablo — EnterNavForm: "Sayılan Miktar" alanında Enter → bir sonraki
                      satırın aynı alanına geçer (kolon boyunca hızlı sayım). Liste sabit
                      olduğundan onRowAdd yok; terminal aksiyon ("Sayımı Tamamla") onaylı/yarı
                      geri-alınamaz olduğundan onSubmit de bağlanmaz (son satırda Enter no-op). */}
                  <EnterNavForm style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ background: colors.backgroundAlt }}>
                          <th style={{ ...thStyle, width: '35px' }}>#</th>
                          <th style={thStyle}>Malzeme</th>
                          <th style={{ ...thStyle, width: '70px' }}>Birim</th>
                          <th style={{ ...thStyle, width: '110px', textAlign: 'right' }}>Sistem Stok</th>
                          <th style={{ ...thStyle, width: '130px', textAlign: 'center', background: '#fff8e1' }}>Sayılan Miktar</th>
                          <th style={{ ...thStyle, width: '100px', textAlign: 'right' }}>Fark</th>
                          <th style={{ ...thStyle, width: '180px' }}>Açıklama</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStoklar.map((s, index) => {
                          const fark = getFark(s.malzeme_id, s.miktar);
                          const farkRenk = fark === null ? '#999' : fark > 0.001 ? styles.success : fark < -0.001 ? styles.danger : '#999';
                          const satirBg = fark !== null && Math.abs(fark) >= 0.001
                            ? (fark > 0 ? '#f1f8e9' : '#fce4ec')
                            : 'white';

                          return (
                            <tr key={s.malzeme_id} data-enter-row style={{ borderBottom: `1px solid ${colors.border}`, background: satirBg }}>
                              <td style={tdStyle}>{index + 1}</td>
                              <td style={{ ...tdStyle, fontWeight: '500' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                  {s.malzeme_kod && <span style={{ color: '#555', fontSize: '11px', fontFamily: 'monospace', fontWeight: 600, padding: '1px 6px', background: '#eef2f5', borderRadius: 4 }}>{s.malzeme_kod}</span>}
                                  <span>{s.malzeme_ad}</span>
                                  {(() => {
                                    const conf = kategoriRozet(s.kategori);
                                    return conf ? (
                                      <span style={{
                                        fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: 10,
                                        background: conf.bg, color: conf.fg,
                                      }}>{conf.label}</span>
                                    ) : null;
                                  })()}
                                  {s.stok_kaydi_yok && (
                                    <span title="Bu lokasyonda hiç stok hareketi yok — sayımla ilk kayıt oluşacak"
                                          style={{
                                            fontSize: '10px', fontWeight: 600, padding: '2px 6px', borderRadius: 10,
                                            background: '#fffde7', color: '#827717', border: '1px dashed #c5b358',
                                          }}>
                                      ✨ Yeni — henüz stoklanmamış
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'center', color: colors.textSecondary }}>{s.birim}</td>
                              <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: styles.secondary }}>
                                {s.miktar.toFixed(2)}
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'center', background: '#fffde7' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  data-enter-nav="row-add"
                                  value={sayimData[s.malzeme_id] ?? ''}
                                  onChange={(e) => handleSayilanChange(s.malzeme_id, e.target.value)}
                                  placeholder="-"
                                  style={{
                                    width: '100%', padding: '6px 8px', border: `1px solid ${colors.border}`,
                                    borderRadius: radius.sm, fontSize: '14px', textAlign: 'right',
                                    boxSizing: 'border-box',
                                    background: sayimData[s.malzeme_id] !== undefined && sayimData[s.malzeme_id] !== '' ? '#fff' : '#fafafa'
                                  }}
                                />
                              </td>
                              <td style={{
                                ...tdStyle, textAlign: 'right', fontWeight: 'bold', color: farkRenk, fontSize: '14px'
                              }}>
                                {fark === null ? '-' : (
                                  <span>
                                    {fark > 0.001 ? '+' : ''}{fark.toFixed(2)}
                                    {Math.abs(fark) >= 0.001 && (
                                      <span style={{
                                        display: 'inline-block', marginLeft: '6px', padding: '1px 6px',
                                        borderRadius: radius.lg, fontSize: '10px', fontWeight: 'bold',
                                        background: fark > 0 ? '#e8f5e9' : '#ffebee',
                                        color: fark > 0 ? '#2e7d32' : '#c62828'
                                      }}>
                                        {fark > 0 ? 'FAZLA' : 'EKSİK'}
                                      </span>
                                    )}
                                  </span>
                                )}
                              </td>
                              <td style={tdStyle}>
                                <input
                                  type="text"
                                  value={aciklamaData[s.malzeme_id] || ''}
                                  onChange={(e) => handleAciklamaChange(s.malzeme_id, e.target.value)}
                                  placeholder="Not..."
                                  style={{
                                    width: '100%', padding: '4px 8px', border: `1px solid ${colors.border}`,
                                    borderRadius: radius.sm, fontSize: '12px', boxSizing: 'border-box'
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </EnterNavForm>

                  {/* Aksiyon butonları */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px' }}>
                    <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                      {sayilanKalem > 0 && (
                        <span>{sayilanKalem} kalem sayıldı, {farkliKalemler.length} kalemde fark var</span>
                      )}
                    </div>
                    <div>
                      {!taslakBanner && !sayimId && (
                        <button
                          onClick={handleYeniBaslat}
                          disabled={!selectedLokasyon || saving}
                          style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
                        >
                          Sayım Başlat
                        </button>
                      )}
                      {sayimId && (
                        <div style={{ display: 'flex', gap: 10 }}>
                          <button onClick={handleIptal} style={{ background: '#fff', color: '#c62828', border: '1px solid #c62828', borderRadius: 6, padding: '10px 20px', cursor: 'pointer', fontSize: '13px' }}>
                            Sayımı İptal Et
                          </button>
                          <button
                            onClick={handleTamamla}
                            disabled={saving}
                            style={{ background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, padding: '10px 24px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}
                          >
                            {saving ? 'Kaydediliyor...' : 'Sayımı Tamamla'}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Stiller
const labelStyle = {
  display: 'block', marginBottom: '5px', fontSize: '13px',
  fontWeight: 'bold', color: '#555'
};

const inputStyle = {
  width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`,
  borderRadius: radius.sm, fontSize: '14px', boxSizing: 'border-box'
};

const thStyle = {
  padding: '6px 8px', textAlign: 'left', fontSize: '13px',
  fontWeight: 'bold', color: '#555', borderBottom: '2px solid #dee2e6'
};

const tdStyle = {
  padding: '8px 12px', fontSize: '14px', verticalAlign: 'middle'
};

const thStyleSmall = {
  padding: '6px 10px', textAlign: 'left', fontSize: '12px',
  fontWeight: 'bold', color: '#555'
};

const tdStyleSmall = {
  padding: '5px 10px', fontSize: '12px'
};

const ozetKartStyle = {
  background: colors.backgroundAlt, borderRadius: radius.md, padding: '12px 16px',
  textAlign: 'center', border: '1px solid #e9ecef'
};

const sonucKartStyle = {
  background: colors.surface, borderRadius: radius.md, padding: '10px 14px',
  textAlign: 'center', border: `1px solid ${colors.border}`
};

export default StokSayimPage;
